import * as Sentry from "@sentry/browser";
import {sanitizeHtml} from "./utils/_sanitize-html";

export function whenReady(fn: () => void): void {
    if (document.readyState === "complete" || document.readyState === "interactive") {
        setTimeout(fn, 1);
    } else {
        document.addEventListener("DOMContentLoaded", fn);
    }
}

export function pushToDataLayer(object: object): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(object);
}

export function redirectTo(url: string): void {
    window.location.href = url;
}

// We need this function because :scope selector is not supported in all browsers
function* selectDirectChildrenByTagName(element: HTMLElement, tagName: string): Generator<HTMLElement, null> {
    tagName = tagName.toLowerCase();
    for (let i = 0; i < element.childNodes.length; i++) {
        const node = element.childNodes[i];

        if (node instanceof HTMLElement && node.tagName.toLowerCase() === tagName) {
            yield node;
        }
    }
    return null;
}

export function getDirectChildrenByTagName(element: HTMLElement, tagName: string): HTMLElement | null {
    const generator = selectDirectChildrenByTagName(element, tagName);
    return generator.next().value;
}

export function getAllDirectChildrenByTagName(element: HTMLElement, tagName: string): HTMLElement[] {
    return [...selectDirectChildrenByTagName(element, tagName)];
}

export const getPageLanguage = memoizeZero((): "ru" | "en" => {
    return document.documentElement.getAttribute("lang") as "ru" | "en";
});

export function memoizeZero<R>(fn: () => R): () => R {
    let cache: R | undefined = undefined;
    return () => {
        if (cache !== undefined) {
            return cache;
        }

        cache = fn();
        return cache;
    }
}

export async function copyToClipboard(str: string): Promise<void> {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        return await navigator.clipboard.writeText(str);
    } else {
        const el = document.createElement("textarea");
        el.value = str;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";

        const wrapper = document.querySelector("dialog") ?? document.body;
        wrapper.appendChild(el);
        el.select();
        document.execCommand("copy");
        wrapper.removeChild(el);
    }
}

const absoluteUrlRegex = new RegExp('^(?:[a-z]+:)?//', 'i');
export function isAbsoluteURL(url: string): boolean {
    return absoluteUrlRegex.test(url);
}

export function safe(target: () => void): () => void {
    return () => {
        try {
            target();
        } catch (e) {
            console.error(e);
        }
    };
}

export function runSafe(target: () => void) {
    safe(target)();
}

export function throttle(target: () => void, ms: number): () => void {
    let isThrottled = false;
    let memo: (() => void) | null = null;

    return () => {
        if (isThrottled) {
            memo = target;
            return;
        }

        target();

        isThrottled = true;

        setTimeout(function() {
            isThrottled = false;
            if (memo !== null) {
                memo();
                memo = null;
            }
        }, ms);
    }
}

export function debounce<T extends (...args: any[]) => void>(func: T, delay: number): (...args: Parameters<T>) => void {
    let timer: ReturnType<typeof setTimeout> | null;

    return function(this: ThisParameterType<T>, ...args: Parameters<T>) {
        const context = this;

        clearTimeout(timer as ReturnType<typeof setTimeout>);

        timer = setTimeout(() => {
            func.apply(context, args);
        }, delay);
    };
}

export enum MinWidthBreakpoint {
    Xs = 0,
    Sm = 576,
    Md = 768,
    Lg = 1024,
    Xl = 1200,
}

export function getCurrentMinWidthBreakpoint(): MinWidthBreakpoint {
    const windowWidth = window.innerWidth;

    if (MinWidthBreakpoint.Xl < windowWidth) {
        return MinWidthBreakpoint.Xl;
    } else if (MinWidthBreakpoint.Lg < windowWidth) {
        return MinWidthBreakpoint.Lg;
    } else if (MinWidthBreakpoint.Md < windowWidth) {
        return MinWidthBreakpoint.Md;
    } else if (MinWidthBreakpoint.Sm < windowWidth) {
        return MinWidthBreakpoint.Sm;
    }

    return MinWidthBreakpoint.Xs;
}

export enum SiteVersion {
    Mobile,
    Tablet,
    Desktop,
}

export function getSiteVersion(): SiteVersion {
    if (window.matchMedia(getMinWidthMediaQuery(MinWidthBreakpoint.Lg)).matches) {
        return SiteVersion.Desktop;
    } else if (window.matchMedia(getMinWidthMediaQuery(MinWidthBreakpoint.Md)).matches) {
        return SiteVersion.Tablet;
    } else {
        return SiteVersion.Mobile;
    }
}

export function onSiteVersionChange(callback: (version: SiteVersion) => void, options: { signal?: AbortSignal } = {}): void {
    for (const breakpoint of [MinWidthBreakpoint.Md, MinWidthBreakpoint.Lg]) {
        window.matchMedia(getMinWidthMediaQuery(breakpoint)).addEventListener(
            "change",
            () => callback(getSiteVersion()),
            { signal: options.signal }
        );
    }
}

function getMinWidthMediaQuery(breakpoint: MinWidthBreakpoint): string {
    return `(min-width: ${breakpoint}px)`;
}

export function getClassNameByRegExp(element: HTMLElement, regexp: RegExp): string | null {
    return [...element.classList].find(className => regexp.exec(className)) ?? null;
}

export function getEnumValueByKey<V>(enumObject: Record<string, V>, key: string): V | null {
    return new Map(Object.entries(enumObject)).get(key) ?? null;
}

export function isScrollableElement(element: HTMLElement) {
    return element.clientHeight < element.scrollHeight;
}

export enum ScrollPosition {
    Top,
    Center,
    Bottom
}

export function getScrollableElementPosition(element: HTMLElement): ScrollPosition {
    const ERROR = 3 // In px

    if (element.scrollTop === 0) {
        return ScrollPosition.Top;
    } else if (element.scrollHeight - ERROR < element.scrollTop + element.clientHeight) {
        return ScrollPosition.Bottom;
    } else {
        return ScrollPosition.Center;
    }
}

export enum SwipeAxis {
    X,
    Y,
}

export function getSwipeAxis(deltaX: number, deltaY: number): SwipeAxis {
    return (Math.abs(deltaX) <  Math.abs(deltaY)) ? SwipeAxis.Y : SwipeAxis.X;
}

export enum SwipeDirection {
    Up,
    Down,
    Left,
    Right,
}

export function getSwipeDirection(axis: SwipeAxis, startPoint: number, endPoint: number): SwipeDirection | null {
    const delta = startPoint - endPoint;

    if (delta < 0) {
        return (axis === SwipeAxis.X) ? SwipeDirection.Right : SwipeDirection.Down;
    } else if (0 < delta) {
        return (axis === SwipeAxis.X) ? SwipeDirection.Left : SwipeDirection.Up;
    } else {
        return null;
    }
}

export function preventElementScroll(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
}

export function filterElementsByString(array: HTMLElement[], value: string): void {
    for (const item of array) {
        item.style.display = item.textContent.toLowerCase().includes(value.toLowerCase()) ? "" : "none";
    }
}

export function createElementFromHTML(htmlString: string): HTMLDivElement {
    const div = document.createElement("div");
    div.innerHTML = sanitizeHtml(htmlString.trim());
    return div;
}

export function getFirstNotEmptyPreviousSibling(elem: HTMLElement, selector?: string): HTMLElement|null {
    let sibling: HTMLElement|null = elem.previousElementSibling as HTMLElement;

    while (sibling && sibling.textContent?.trim() === '') {
        sibling = sibling.previousElementSibling as HTMLElement|null;
    }

    if (!sibling) {
        return null;
    }

    if (selector && !sibling.matches(selector)) {
        return null;
    }

    return sibling;
}

export function buildQueryParams(params: object): URLSearchParams {
    return objectToURLSearchParams(params);
}

function objectToURLSearchParams(obj: Record<string, any>, prefix?: string): URLSearchParams {
    const params = new URLSearchParams();

    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            const paramKey = prefix ? `${prefix}[${key}]` : key;

            if (typeof value === 'object' && !Array.isArray(value)) {
                const nestedParams = objectToURLSearchParams(value, paramKey);
                for (const [nestedKey, nestedValue] of nestedParams) {
                    params.append(nestedKey, nestedValue);
                }
            } else if (Array.isArray(value)) {
                value.forEach((val, index) => {
                    params.append(`${paramKey}[${index}]`, val);
                });
            } else {
                params.append(paramKey, value);
            }
        }
    }

    return params;
}

export function getLinkToSelection(selection: Selection): string | null {
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();

    const wordsCount = selectedText.split(/\s+/).length;
    let contextBefore = '';
    let contextAfter = '';

    if (wordsCount <= 3) {
        const startContainer = range.startContainer;
        const endContainer = range.endContainer;

        if (startContainer === endContainer && startContainer.nodeType === Node.TEXT_NODE) {
            const textContent = startContainer.textContent;

            if (textContent === null) {
                return null;
            }

            const startOffset = range.startOffset;
            const endOffset = range.endOffset;

            contextBefore = getTextContext(textContent, startOffset, 'start');
            contextAfter = getTextContext(textContent, endOffset, 'end');
        }
    }

    const textFragment = getTextFragment(selectedText, contextBefore, contextAfter);
    return `${window.location.origin}${window.location.pathname}${textFragment}`;
}

function getTextFragment(text: string, before = '', after = ''): string {
    if (text === '') {
        return '';
    }

    const parts = [
        before ? encodeURIComponent(before) + '-,' : '',
        encodeURIComponent(text),
        after ? ',-' + encodeURIComponent(after) : '',
    ];

    return "#:~:text=" + parts.join('');
}

function getTextContext(text: string, start: number, direction: 'start' | 'end', wordsCount: number = 3): string {
    let context: string;

    switch (direction) {
        case 'start':
            const beforeText = text.slice(0, start).trim().split(/\s+/);
            context = beforeText.slice(-wordsCount).join(' ');
            break;
        case 'end':
            const afterText = text.slice(start).trim().split(/\s+/);
            context = afterText.slice(0, wordsCount).join(' ');
            break;
    }

    return context;
}

export function toggleLoader(isVisible: boolean): void {
    const loader = document.querySelector(".loader");

    if (loader === null) {
        console.error("Loader element not found");
        return;
    }

    if (isVisible) {
        loader.classList.remove("visually-hidden");
    } else {
        loader.classList.add("visually-hidden");
    }
}

export function compareFormData(formData1: FormData, formData2: FormData): boolean {
    const toMap = (formData: FormData) => {
        const map = new Map<string, string[]>();

        for (const [key, value] of formData.entries()) {
            const arr = map.get(key) ?? [];
            arr.push(String(value));
            map.set(key, arr);
        }

        return map;
    };

    const map1 = toMap(formData1);
    const map2 = toMap(formData2);

    if (map1.size !== map2.size) {
        return false;
    }

    for (const [key, values1] of map1) {
        const values2 = map2.get(key);
        if (!values2) {
            return false;
        }

        if (values1.length !== values2.length) {
            return false;
        }

        for (let i = 0; i < values1.length; i++) {
            if (values1[i] !== values2[i]) {
                return false;
            }
        }
    }

    return true;
}

export enum ButtonSizeClass {
    Desktop = "button_size_s",
    Tablet = "button_size_l",
    Mobile = "button_size_m",
}

export function changeButtonSize(button: HTMLElement, sizeClass: ButtonSizeClass): void {
    const currentSizeClass = getClassNameByRegExp(button, /^button_size/);

    if (currentSizeClass) {
        button.classList.replace(currentSizeClass, sizeClass);
    } else {
        button.classList.add(sizeClass);
    }
}

export function getElementBottomClientY(element: HTMLElement): number {
    return element.getBoundingClientRect().y + element.getBoundingClientRect().height;
}

export function getElementTranslateXValue(element: HTMLElement): number {
    const style = getComputedStyle(element);
    const transform = style.transform;

    if (!transform || transform === "none") {
        return 0;
    }

    const MatrixConstructor = window.DOMMatrix || window.WebKitCSSMatrix;

    if (!MatrixConstructor) {
        console.warn("No DOMMatrix/WebKitCSSMatrix available");
        return 0;
    }

    try {
        const matrix = new MatrixConstructor(transform);
        return Number(matrix.m41) || 0;
    } catch (e) {
        console.error("Failed to parse transform matrix:", e);
        return 0;
    }
}

export function getElementMinTranslateXValue(element: HTMLElement): number {
    return -element.scrollWidth + element.clientWidth;
}

export function enableElementTransition(element: HTMLElement): void {
    element.classList.remove("transition-none");
}

export function disableElementTransition(element: HTMLElement): void {
    element.classList.add("transition-none");
}

export function isElementOrFirstScreenHeightScrolled(element: HTMLElement | null): boolean {
    const activatePointBottomClientY = element instanceof HTMLElement
        ? getElementBottomClientY(element)
        : window.innerHeight - window.scrollY;

    return activatePointBottomClientY < 0;
}

export function toggleElementVisibility(element: HTMLElement, toShow: boolean): void {
    if (toShow) {
        showElement(element);
    } else {
        hideElement(element);
    }
}

export function showElement(element: HTMLElement): void {
    element.classList.remove("hidden");
}

export function hideElement(element: HTMLElement): void {
    element.classList.add("hidden");
}

export function filterNullValues<T extends object>(object: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(object).filter(([, value]) => value!== null)
    ) as Partial<T>;
}

export function muteElement(element: HTMLElement): void {
    element.classList.add("mute");
}

export function unmuteElement(element: HTMLElement): void {
    element.classList.remove("mute");
}

export function isPluginInitialized(element: HTMLElement): boolean {
    return element.hasAttribute("data-plugin-initialized");
}

export function setPluginInitialized(element: HTMLElement): void {
    element.setAttribute("data-plugin-initialized", "");
}

type ElementAlterer<T extends HTMLElement> = (element: T) => void;
type ElementVariants<T extends HTMLElement> = { [key: string]: ElementAlterer<T> };

type MeasurementResultKey<T> = T extends `to${infer Rest}`
    ? `is${Rest}Overflows`
    : never;

type MeasurementResult<T extends ElementVariants<any>> = Record<MeasurementResultKey<keyof T>, boolean>;

export function observeElementVariantsOverflow<T extends ElementVariants<HTMLElement>>(
    element: HTMLElement,
    variants: T,
    callback: (measurements: MeasurementResult<T>) => void
): () => void {
    const parentElement = element.parentElement;
    if (parentElement === null) {
        throw new Error("Failed to observe element variants overflow: provided element is not a child of another element");
    }

    const resizeObserver = new ResizeObserver(() => {
        const measurements: Record<string, boolean> = {};

        for (const [name, alterer] of Object.entries(variants)) {
            const prepared = prepareElementForMeasurement(element, alterer);

            // this is needed to prevent checked inputs reset
            const childrenWithNameAttribute = prepared.querySelectorAll("[name]");
            for (const element of childrenWithNameAttribute) {
                element.removeAttribute("name");
            }

            parentElement.appendChild(prepared);
            measurements[`is${name.substring(2)}Overflows`] = prepared.scrollWidth > prepared.clientWidth;
            parentElement.removeChild(prepared);
        }

        callback(measurements as MeasurementResult<T>);
    });

    resizeObserver.observe(element);

    return () => {
        resizeObserver.unobserve(element);
    };
}

function prepareElementForMeasurement<T extends HTMLElement>(element: T, alterElement: ElementAlterer<T>): T {
    const clone = element.cloneNode(true) as T;

    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';

    alterElement(clone);

    return clone;
}

export function scrollToTopOfElementInMobileAndTablet(element: HTMLElement): void {
    if (([SiteVersion.Mobile, SiteVersion.Tablet] as SiteVersion[]).includes(getSiteVersion())) {
        element.scrollIntoView({behavior: "smooth"});
    }
}

export function getRandomArrayElement<T>(array: T[]): T | undefined {
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
}

export function showButtonLoader(button: HTMLButtonElement): void {
    button.style.transition = "none";
    button.classList.add("button_loader");
}

export function hideButtonLoader(button: HTMLButtonElement): void {
    button.style.transition = "";
    button.classList.remove("button_loader");
}

export function setButtonDisabled(button: HTMLElement | null | undefined, disabled: boolean): void {
    if (!button) {
        return;
    }

    const isLink = button instanceof HTMLAnchorElement;

    if (disabled) {
        button.setAttribute("disabled", "");
        if (isLink) {
            button.setAttribute("aria-disabled", "true");
            button.setAttribute("tabindex", "-1");
        }
    } else {
        button.removeAttribute("disabled");
        if (isLink) {
            button.removeAttribute("aria-disabled");
            button.removeAttribute("tabindex");
        }
    }
}

export function getFilenameWithoutExtension(url: string): string {
  const parts = url.split('/');
  const filename = parts[parts.length - 1];
  return filename.replace(/\.[^/.]+$/, '');
}

export function getCsrfToken(): string|null {
    return document.head.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? null;
}

export function getScrollPercent(): number {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;

    if (scrollHeight <= 0) {
        return 100;
    }

    return Math.round((scrollTop / scrollHeight) * 100);
}

export function downloadBlob(blob: Blob, filename: string) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
}

export function getFilenameFromResponse(response: Response, fallback: string): string {
    const disposition = response.headers.get("Content-Disposition");
    if (!disposition) {
        return fallback;
    }

    const encodedFilenameMatch = disposition.match(/filename\*\s*=\s*[^']*''(.+)/);
    if (encodedFilenameMatch) {
        return decodeURIComponent(encodedFilenameMatch[1]);
    }

    const filenameMatch = disposition.match(/filename\s*=\s*"?(.*?)"?$/);
    if (filenameMatch) {
        return filenameMatch[1];
    }

    return fallback;
}

export function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }
    return String(error);
}

export function pxToRem(px: number): number {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Math.round((px / rootFontSize) * 100) / 100;
}

export function assertExists<T>(value: T | null | undefined, errorMessage: string): T {
    if (value === null || value === undefined) {
        throw new Error(errorMessage);
    }

    return value;
}